import { useEffect, useMemo, useRef, useState } from "react";

export interface Choice {
  id: string;
  /** What is shown, and what a search is matched against. */
  label: string;
  /** Matched as well as the label, without being displayed — a VIN, a phone
      number. Lets someone find a car by typing the last six of its VIN. */
  extra?: string;
}

/**
 * A select you can type into.
 *
 * A native dropdown is fine for six options and useless for sixty: no search,
 * and on a laptop it opens a list taller than the screen. This keeps the same
 * shape as the other fields but filters as you type.
 *
 * Matching is on every word separately, so "escape 2017" finds the same car
 * as "2017 escape". Word order is not something anyone should have to guess.
 */
export default function SearchSelect({
  label, value, onChange, choices, placeholder = "Choose", hint, error, action,
}: {
  label: string;
  value: string;
  onChange: (id: string) => void;
  choices: Choice[];
  placeholder?: string;
  hint?: string;
  error?: string | null;
  /** An extra row pinned to the top, for "+ Add a vehicle". */
  action?: { id: string; label: string };
}) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [cursor, setCursor] = useState(0);
  const wrap = useRef<HTMLDivElement>(null);
  const input = useRef<HTMLInputElement>(null);

  const chosen = choices.find((c) => c.id === value) ?? null;

  const matches = useMemo(() => {
    const words = query.trim().toLowerCase().split(/\s+/).filter(Boolean);
    if (!words.length) return choices;
    return choices.filter((c) => {
      const hay = `${c.label} ${c.extra ?? ""}`.toLowerCase();
      return words.every((w) => hay.includes(w));
    });
  }, [choices, query]);

  useEffect(() => { setCursor(0); }, [query, open]);

  // Clicking anywhere else closes it without choosing.
  useEffect(() => {
    if (!open) return;
    const away = (e: MouseEvent) => {
      if (!wrap.current?.contains(e.target as Node)) { setOpen(false); setQuery(""); }
    };
    document.addEventListener("mousedown", away);
    return () => document.removeEventListener("mousedown", away);
  }, [open]);

  const rows = action ? [{ id: action.id, label: action.label }, ...matches] : matches;

  function pick(id: string) {
    onChange(id);
    setOpen(false);
    setQuery("");
  }

  function onKey(e: React.KeyboardEvent) {
    if (e.key === "ArrowDown") { e.preventDefault(); setCursor((c) => Math.min(c + 1, rows.length - 1)); }
    else if (e.key === "ArrowUp") { e.preventDefault(); setCursor((c) => Math.max(c - 1, 0)); }
    else if (e.key === "Enter") { e.preventDefault(); const r = rows[cursor]; if (r) pick(r.id); }
    else if (e.key === "Escape") { setOpen(false); setQuery(""); }
  }

  const id = `ss-${label.replace(/\W+/g, "-").toLowerCase()}`;

  return (
    <div className="field" ref={wrap}>
      <label htmlFor={id}>{label}</label>
      <div className="ss">
        <input
          id={id} ref={input} className="ss-input"
          value={open ? query : (chosen?.label ?? "")}
          placeholder={chosen ? chosen.label : placeholder}
          onChange={(e) => { setQuery(e.target.value); setOpen(true); }}
          onFocus={() => { setOpen(true); setQuery(""); }}
          onKeyDown={onKey}
          /* Unrecognised token: stops the browser offering its own saved
             values on top of this list. */
          autoComplete="list-search"
          data-1p-ignore data-lpignore="true"
        />
        <span className="ss-caret" aria-hidden="true">⌄</span>
      </div>

      {open && (
        <ul className="ss-list" role="listbox">
          {rows.length === 0 && <li className="ss-none">Nothing matches that</li>}
          {rows.map((r, i) => (
            <li key={r.id}>
              <button type="button"
                      className={`ss-item ${i === cursor ? "on" : ""} ${r.id === value ? "picked" : ""}`}
                      onMouseEnter={() => setCursor(i)}
                      onMouseDown={(e) => e.preventDefault()}
                      onClick={() => pick(r.id)}>
                {r.label}
              </button>
            </li>
          ))}
        </ul>
      )}

      {error ? <p className="err">{error}</p>
             : hint ? <p className="hint">{hint}</p> : null}
    </div>
  );
}
